import React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

export function InternalPageHeader({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  children?: React.ReactNode; // For optional image/card overlap
}) {
  return (
    <section
      className={cn(
        "relative w-full overflow-hidden flex flex-col items-center justify-center",
        "pb-32 min-h-[420px] sm:rounded-3xl mx-auto",
        // Use large top padding to clear the navbar (64px) + extra margin
        "pt-[calc(64px+5rem)] sm:pt-[calc(64px+7rem)] md:pt-[calc(64px+8rem)]"
      )}
    >
      {/* Vignette/radial overlay for depth */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_60%_40%,rgba(0,0,0,0.18)_0,transparent_70%)]" />
      </div>
      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 text-center">
        <motion.h1
          className="text-6xl sm:text-7xl font-extrabold tracking-tight text-white"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.7 }}
        >
          {title}
        </motion.h1>
        {description && (
          <motion.p
            className="mt-6 text-2xl text-white/90 font-light"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            {description}
          </motion.p>
        )}
        {actions && (
          <div className="flex justify-center gap-4 mt-10">
            {actions}
          </div>
        )}
      </div>
      {/* Optional: Overlapping card/image */}
      {children && (
        <div className="relative z-20 -mb-24 mt-12 w-full flex justify-center">
          {children}
        </div>
      )}
      {/* Large rounded corners on desktop */}
      <style>{`
        @media (min-width: 1024px) {
          section {
            border-radius: 2.5rem;
          }
        }
      `}</style>
    </section>
  );
} 